import { useState } from "react";
import { Share2, Mail, Linkedin, Twitter, Link2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export function ShareButtons({ 
  title = "Check out AskIsaac", 
  description = "Healthcare's Complete Procurement & Maintenance Solution",
  url = "https://sterilemate.com",
  variant = "default", // "default" | "dark" | "light"
  size = "sm"
}) {
  const [copied, setCopied] = useState(false);

  const shareText = `${title} - ${description}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    email: `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`${description}\n\nLearn more: ${url}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for non-secure contexts or older browsers
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy link. Please copy manually: " + url);
    }
  };

  const handleShare = (platform) => {
    if (platform === "copy") {
      handleCopyLink();
    } else {
      window.open(shareLinks[platform], "_blank", "noopener,noreferrer");
    }
  };

  const buttonStyles = {
    default: "text-slate-400 hover:text-white hover:bg-slate-700",
    dark: "text-slate-300 hover:text-white hover:bg-slate-800 border-slate-700",
    light: "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-slate-200"
  };

  const menuStyles = {
    default: "bg-slate-800 border-slate-700",
    dark: "bg-slate-900 border-slate-700",
    light: "bg-white border-slate-200"
  };

  const itemStyles = {
    default: "text-slate-300 hover:text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white",
    dark: "text-slate-300 hover:text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white",
    light: "text-slate-700 hover:text-slate-900 hover:bg-slate-100 focus:bg-slate-100"
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size={size}
          className={buttonStyles[variant]}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className={`w-48 ${menuStyles[variant]}`}
      >
        <DropdownMenuItem 
          onClick={() => handleShare("email")}
          className={`cursor-pointer ${itemStyles[variant]}`}
        >
          <Mail className="w-4 h-4 mr-3 text-blue-400" />
          Email
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare("linkedin")}
          className={`cursor-pointer ${itemStyles[variant]}`}
        >
          <Linkedin className="w-4 h-4 mr-3 text-blue-500" />
          LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare("twitter")}
          className={`cursor-pointer ${itemStyles[variant]}`}
        >
          <Twitter className="w-4 h-4 mr-3 text-sky-400" />
          X (Twitter)
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare("copy")}
          className={`cursor-pointer ${itemStyles[variant]}`}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-3 text-emerald-400" />
              Copied!
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4 mr-3 text-slate-400" />
              Copy Link
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ShareButtons;
